import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'

import { CategoryFragment } from '@/generated/contentful'

import { getBaseUrl } from '@/lib/contentful/utils'

import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import { useUrl } from '@/utils/url/useUrl'

import { useEntry } from '../Category.hooks'

type Props = {
	entryItem: CategoryFragment
}

export function CategoryMeta({ entryItem }: Props) {
	const { locale = 'en-US', asPath } = useRouter()
	const entry = useEntry()
	const url = useUrl({ locale })

	const baseUrl = `${url?.protocol}//${url?.host}`

	const path = asPath?.split('?').at(0)

	let category = entryItem

	if ('error' in entry && !entryItem) return null
	if ('data' in entry) category = entry.data

	const { seoMetadata } = category

	const includeTitleTemplate = Boolean(seoMetadata?.includeTitleTemplate)
	const title = includeTitleTemplate ? `${seoMetadata?.title} | BigCommerce` : seoMetadata?.title
	return (
		<NextSeo
			title={title ?? undefined}
			description={seoMetadata?.description ?? undefined}
			canonical={`${baseUrl}${path}`}
			languageAlternates={[
				{ hrefLang: 'en-au', href: `${getBaseUrl('en-AU')}${path}` },
				{ hrefLang: 'en-gb', href: `${getBaseUrl('en-GB')}${path}` },
				{ hrefLang: 'en-us', href: `${getBaseUrl('en-US')}${path}` },
				{ hrefLang: 'x-default', href: `${getBaseUrl()}${path}` }
			]}
			openGraph={{
				title: title ?? undefined,
				description: seoMetadata?.description ?? undefined,
				url: `${baseUrl}${path}`,
				siteName: url?.href.includes('/blog') ? 'The BigCommerce Blog' : 'BigCommerce',
				images: seoMetadata?.image?.url
					? [
							{
								url: seoMetadata?.image?.url,
								width: seoMetadata?.image?.width,
								height: seoMetadata?.image?.height,
								alt: 'Bigcommerce logo',
								type: 'image/jpeg'
							}
					  ]
					: undefined,
				locale: getSeoLocale(locale)
			}}
			nofollow={Boolean(seoMetadata?.nofollow)}
			noindex={Boolean(seoMetadata?.noindex)}
		/>
	)
}
